import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { CSSProperties, useState } from 'react';
import {
  FormattedMessage,
  FormattedNumber,
  MessageDescriptor,
  useIntl,
} from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { SimpleRadio } from '@sorare/core/src/atoms/inputs/SimpleRadio';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandL,
  BrandS,
  BrandXXS,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { EdgeRectangleShape } from '@sorare/core/src/atoms/ui/EdgeRectangleShape';
import { RhomboidRainbowContainer } from '@sorare/core/src/atoms/ui/RhomboidRainbowContainer';
import { CardBack } from '@sorare/core/src/components/card/Back';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  MonetaryAmount,
  monetaryAmountFragment,
} from '@sorare/core/src/lib/monetaryAmount';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { InfoDialog } from 'components/commonGame/InfoDialog';

import type { ChooseGameMode_conversionCredit } from './__generated__/index.graphql';
import commonBg from './assets/commonBackground.png';
import kylian from './assets/kylian.png';
import limitedBg from './assets/limitedBackground.png';

const Root = styled(Container)`
  min-height: 100%;
  --max-cell-width: calc(80 * var(--unit));

  &.withBackground {
    background: linear-gradient(to bottom, transparent 60%, #1e4ea9 100%),
      linear-gradient(180deg, #2c2b70 0%, rgba(44, 43, 112, 0) 100%);
    --header-height: calc(5 * var(--unit));
  }
`;
const Header = styled(Vertical).attrs({ center: true, gap: 2 })`
  position: relative;
  padding-bottom: var(--double-unit);
  width: 100%;
  max-width: var(--max-cell-width);
`;
const InnerWrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit) 0;
  align-items: center;
`;
const InfoButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;
const ItemWrapper = styled.label`
  width: 100%;
  max-width: var(--max-cell-width);
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: var(--double-unit);
  border: 1px solid var(--c-nd-300-opaque);
  border-radius: var(--double-unit);
  color: var(--c-white);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.1s ease-in-out;
  &.selected {
    outline: 2px solid var(--selected-border-color);
    box-shadow: 0 0 8px var(--selected-border-color);
  }

  &:hover {
    transform: scale(1.02);
  }
`;
const CommonItemWrapper = styled(ItemWrapper)`
  --selected-border-color: #d1dcdf;
  background:
    linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.6) 59.65%,
      rgba(42, 42, 42, 0.6) 100%
    ),
    url(${commonBg}) no-repeat center / cover;
`;
const LimitedItemWrapper = styled(ItemWrapper)`
  --selected-border-color: #dfad46;
  height: 362px;
  background:
    linear-gradient(180deg, rgba(1, 1, 1, 0) 53.05%, #010101 100%),
    url(${kylian}) no-repeat center var(--quadruple-unit) / auto 326px,
    url(${limitedBg}) no-repeat center / cover,
    linear-gradient(0deg, rgba(0, 0, 0, 0.6) 71.53%, rgba(70, 47, 3, 0.6) 100%);
`;
const StyledBrandXXS = styled(BrandXXS)`
  text-shadow:
    0px 0px 2px var(--c-black),
    0px 0px 8px var(--c-nl-800);
`;
const LimitedTextOffset = styled.div`
  padding-top: var(--unit);
`;
const MainTextWrapper = styled.div`
  position: relative;
`;
const FreeCardsMainTextWrapper = styled(MainTextWrapper)`
  align-self: stretch;
`;
const EllipsisShadow = styled.div`
  position: absolute;
  inset: 0;
  filter: blur(7px);
  border-radius: 50%;
  background: linear-gradient(299deg, #f096fd 17.38%, #adeef9 80.97%);
`;
const FreeCardsEllipsisShadow = styled(EllipsisShadow)`
  background: linear-gradient(299.16deg, #b9c6c9 17.38%, #5f686a 80.97%);
`;
const RadioWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  left: var(--double-unit);
  color: var(--c-nd-600);

  &.hidden {
    visibility: hidden;
  }
`;
const Footer = styled(Vertical).attrs({ center: true })`
  isolation: isolate;
  padding: var(--unit) var(--intermediate-unit);
  backdrop-filter: blur(4px);
  background: var(--footer-background);
  align-self: stretch;
`;
const FooterText = styled(BrandS)`
  z-index: 1;
`;
const CardBacks = styled(Horizontal)`
  z-index: 0;
  isolation: isolate;
  position: absolute;
  bottom: calc(-1 * var(--unit));
  left: var(--double-unit);
  transition: transform 0.3s ease-in-out;
  transform: translateY(18px);
  &.selected {
    transform: translateY(2px);
  }
`;
const FirstCardBack = styled(CardBack)`
  height: 70px;
  transform: rotate(-16deg);
  z-index: 1;
`;
const SecondCardBack = styled(CardBack)`
  height: 57px;
  transform: rotate(9deg) translateX(-22px) translateY(7px);
  z-index: 0;
`;
const ItemHeader = styled.div`
  position: relative;
  height: var(--header-height);
  align-self: stretch;
`;
const FreeCardsWrapper = styled.div`
  transform: skew(-10deg);
  --border-gradient: linear-gradient(311.37deg, #bcc8cb 2.08%, #5e6769 84.7%);
  --border-size: 2px;
  padding: var(--unit) var(--double-unit);
  border-radius: var(--half-unit);
  background: linear-gradient(274deg, #2a2a2a 50.53%, #000 105.68%);
  box-shadow:
    0 0 4px 0 var(--c-black),
    0 0 8px 0 var(--c-black);
  & > * {
    transform: skew(10deg);
  }
  ${borderGradientStyle};
`;
const PromoWrapper = styled.div`
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
`;
const StyledHorizontal = styled(Horizontal).attrs({ center: true })`
  height: var(--quadruple-unit);
`;
const StyledImg = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;
`;
const TapInstructionWrapper = styled.div`
  padding: var(--double-unit) var(--quadruple-unit);
`;
const ButtonWrapper = styled.div`
  position: sticky;
  bottom: var(--double-unit);
  padding: 0 var(--quadruple-unit);
  align-self: center;
`;

type Props = {
  onGoPro: () => void;
  onChooseCommon?: () => void;
  goProMode?: boolean;
  conversionCredit: Nullable<ChooseGameMode_conversionCredit>;
  tapInstructionMessage: MessageDescriptor;
};
export const ChooseGameMode = ({
  onGoPro,
  onChooseCommon,
  goProMode,
  conversionCredit,
  tapInstructionMessage,
}: Props) => {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const track = useEvents();
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState<
    typeof Rarity.common | typeof Rarity.limited | null
  >(null);
  const commonSelected = selected === Rarity.common;
  const limitedSelected = selected === Rarity.limited;
  const { main: maxDiscountWithConversion } = useAmountWithConversion({
    monetaryAmount: conversionCredit?.maxDiscount || MonetaryAmount.zero,
    hideFractionDigits: true,
  });
  const showTapInstructions = !goProMode && !selected;
  const showSelectButton = !goProMode && selected;

  const { usd, getUserFiatAmountInMajorUnit } = useMonetaryAmount();
  const {
    fiatCurrency: { code: userFiatCurrencyCode },
  } = useCurrentUserContext();
  const cashPrizeInDollars = 10000000;
  const cashPrizeMonetaryAmount = usd(cashPrizeInDollars * 100);

  return (
    <>
      <Root className={classNames({ withBackground: !goProMode })}>
        <InnerWrapper>
          <Header>
            <StarBall />
            <BrandL className="text-center">
              {goProMode ? (
                <FormattedMessage {...glossary.goPro} />
              ) : (
                <FormattedMessage
                  id="ChooseGameMode.title"
                  defaultMessage="Choose your game mode"
                />
              )}
            </BrandL>
            <InfoButtonWrapper>
              <IconButton
                disableDebounce
                color="transparent"
                size="small"
                onClick={() => {
                  setInfoDialogOpen(true);
                }}
                title={formatMessage(glossary.learnMore)}
                icon={faInfoCircle}
              />
            </InfoButtonWrapper>
          </Header>
          <LimitedItemWrapper
            className={classNames({ selected: limitedSelected })}
          >
            <ItemHeader>
              {!!conversionCredit && (
                <PromoWrapper>
                  <EdgeRectangleShape
                    shape="inverted-trapezoid"
                    padding="0 var(--quadruple-unit)"
                    offset="var(--triple-unit)"
                    background="#111F1C"
                    border="linear-gradient(to bottom,  #111F1C 0%, #111F1C 30%, transparent 100%), linear-gradient(to right, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8)"
                  >
                    <StyledHorizontal>
                      <StyledImg
                        src={`${FRONTEND_ASSET_HOST}/rewards/credit.png`}
                        alt=""
                      />
                      <BrandS>
                        <FormattedMessage
                          id="ChooseGameMode.discount"
                          defaultMessage="{amount} off"
                          values={{ amount: maxDiscountWithConversion }}
                        />
                      </BrandS>
                    </StyledHorizontal>
                  </EdgeRectangleShape>
                </PromoWrapper>
              )}
              <RadioWrapper
                className={classNames({
                  hidden: goProMode,
                })}
              >
                <SimpleRadio
                  id="limited-radio"
                  checked={limitedSelected}
                  checkedColor="var(--c-white)"
                  onClick={() => {
                    if (goProMode) {
                      onGoPro();
                    } else if (!limitedSelected) {
                      setSelected(Rarity.limited);
                    }
                  }}
                />
              </RadioWrapper>
            </ItemHeader>
            <Vertical center>
              <LimitedTextOffset>
                <MainTextWrapper>
                  <EllipsisShadow />
                  <RhomboidRainbowContainer>
                    <BrandS className="text-center">
                      <FormattedMessage
                        id="UnlockPro.shortCashPrizeDescription"
                        defaultMessage="{amount} cash prizes*"
                        values={{
                          amount: (
                            <FormattedNumber
                              style="currency"
                              currency={userFiatCurrencyCode}
                              value={getUserFiatAmountInMajorUnit(
                                cashPrizeMonetaryAmount
                              )}
                              currencyDisplay="narrowSymbol"
                              notation="compact"
                              maximumFractionDigits={0}
                            />
                          ),
                        }}
                      />
                    </BrandS>
                  </RhomboidRainbowContainer>
                </MainTextWrapper>
              </LimitedTextOffset>
              <Horizontal gap={1.5} className="text-center">
                <StyledBrandXXS>
                  <FormattedMessage
                    id="ChooseGameMode.pro.firstSubtitle"
                    defaultMessage="Buy Cards"
                  />
                </StyledBrandXXS>
                <StyledBrandXXS>
                  <FormattedMessage
                    id="ChooseGameMode.pro.secondSubtitle"
                    defaultMessage="Win Cash"
                  />
                </StyledBrandXXS>
              </Horizontal>
            </Vertical>
            <Footer
              style={
                {
                  '--footer-background': 'rgba(247, 177, 0, 0.20)',
                } as CSSProperties
              }
            >
              <CardBacks
                className={classNames({
                  selected: limitedSelected,
                })}
              >
                <FirstCardBack rarity={Rarity.limited} />
                <SecondCardBack rarity={Rarity.limited} />
              </CardBacks>
              <FooterText>
                <FormattedMessage {...glossary.pro} />
              </FooterText>
            </Footer>
          </LimitedItemWrapper>
          {!goProMode && (
            <CommonItemWrapper
              className={classNames({ selected: commonSelected })}
            >
              <ItemHeader>
                <RadioWrapper>
                  <SimpleRadio
                    id="common-radio"
                    checked={commonSelected}
                    checkedColor="var(--c-white)"
                    onClick={() => {
                      if (!commonSelected) {
                        setSelected(Rarity.common);
                      }
                    }}
                  />
                </RadioWrapper>
              </ItemHeader>
              <Vertical center>
                <FreeCardsMainTextWrapper>
                  <FreeCardsEllipsisShadow />
                  <FreeCardsWrapper>
                    <BrandS className="text-center">
                      <FormattedMessage
                        id="ChooseGameMode.common.freePacks"
                        defaultMessage="Free Packs"
                      />
                    </BrandS>
                  </FreeCardsWrapper>
                </FreeCardsMainTextWrapper>
                <Horizontal gap={1.5}>
                  <BrandXXS>
                    <FormattedMessage
                      id="ChooseGameMode.common.firstSubtitle"
                      defaultMessage="Daily Packs"
                    />
                  </BrandXXS>
                  <BrandXXS>
                    <FormattedMessage
                      id="ChooseGameMode.common.secondSubtitle"
                      defaultMessage="Learn the game"
                    />
                  </BrandXXS>
                </Horizontal>
              </Vertical>
              <Footer
                style={
                  {
                    '--footer-background': 'rgba(209, 220, 223, 0.20)',
                  } as CSSProperties
                }
              >
                <CardBacks
                  className={classNames({
                    selected: commonSelected,
                  })}
                >
                  <FirstCardBack rarity={Rarity.common} />
                  <SecondCardBack rarity={Rarity.common} />
                </CardBacks>
                <FooterText>
                  <FormattedMessage
                    id="ChooseGameMode.common.footer"
                    defaultMessage="Beginner"
                  />
                </FooterText>
              </Footer>
            </CommonItemWrapper>
          )}
          {!!showTapInstructions && (
            <TapInstructionWrapper>
              <LabelL color="var(--c-nd-600)" className="text-center" bold>
                <FormattedMessage {...tapInstructionMessage} />
              </LabelL>
            </TapInstructionWrapper>
          )}
          {!!showSelectButton && (
            <ButtonWrapper>
              <Button
                color="secondary"
                size="large"
                onClick={() => {
                  track('Click Continue In Choose Your Level', {
                    scarcity: selected,
                  });
                  if (commonSelected) {
                    onChooseCommon?.();
                  } else {
                    onGoPro();
                  }
                }}
              >
                <FormattedMessage
                  id="ChooseGameMode.button"
                  defaultMessage="Select Level"
                />
              </Button>
            </ButtonWrapper>
          )}
        </InnerWrapper>
      </Root>
      <InfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
      />
    </>
  );
};

ChooseGameMode.fragments = {
  conversionCredit: gql`
    fragment ChooseGameMode_conversionCredit on ConversionCredit {
      id
      maxDiscount {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<ChooseGameMode_conversionCredit>,
};
